/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-13 22:12:01
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, TextStyle } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { getShop, isStoreAvailablePersonalizationCampaign } from 'src/helpers';

import { updateTenantApp, updateLocalTenantApp } from 'actions/oauth-actions';

import PersonalizationMarketingCampaign01 from 'src/assets/images/campaigns/personalization-present-banner-01.png';
import PersonalizationMarketingCampaign02 from 'src/assets/images/campaigns/personalization-present-banner-02.png';
import arrowRight from 'src/assets/images/campaigns/arrow-right-minor.png';

const MarketingCampaignBanner = props => {
  const { i18n, page, showWhenDismissed, tenantApp, type, updateTenantAppLoading } = props;
  const [dismissedBanner, setDismissedBanner] = useState(true);

  const getKeyDismiss = shop => `${shop}-dismissMarketingCampaignBanner-${page}`;

  const checkDismissedBanner = () => {
    const shop = getShop();
    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
    const personalizationCampaignStatus = _.get(tenantApp, 'personalizationCampaignStatus', '');
    const firstInstalledAt = _.get(tenantApp, 'firstInstalledAt');

    // Checking  selected plan or install after 18/06
    if (firstInstalledAt > 1718668800 || !isStoreAvailablePersonalizationCampaign(selectedPlan)) {
      return;
    }

    // Check time and status
    const now = moment().startOf();
    const isShowTime =
      now.isAfter(moment('2024-06-18 00:00:00')) && now.isBefore(moment('2024-06-24 23:59:59')); // Date to test

    if (!isShowTime || !_.isEmpty(personalizationCampaignStatus)) {
      return;
    }

    try {
      const dismissed = localStorage.getItem(getKeyDismiss(shop));
      if (dismissed !== 'true') setDismissedBanner(false);
    } catch (error) {}
  };

  const onDismiss = () => {
    const shop = getShop();
    try {
      localStorage.setItem(getKeyDismiss(shop), true);
    } catch (error) {}

    setDismissedBanner(true);
  };

  // Handle change personalization campaign status
  const handleRegisterCampaign = () => {
    const { updateTenantApp, updateLocalTenantApp } = props;
    const meta = {
      onSuccess: () => {
        setDismissedBanner(true);
        updateLocalTenantApp({ showConfirmedPersonalizationCampaignPopup: true });
      }
    };
    updateTenantApp({ personalizationCampaignStatus: 'registered' }, meta);
  };

  useEffect(() => {
    checkDismissedBanner();
    // eslint-disable-next-line
  }, []);

  if (dismissedBanner) {
    return showWhenDismissed;
  }

  const renderHorizontal = () => (
    <div className={`BC-MarketingCampaignBanner ${page}`}>
      <div className="BC-MarketingCampaignBanner-inner">
        <div className="banner-dismiss">
          <Button onClick={onDismiss} icon={CancelSmallMinor} plain />
        </div>
        <div className="banner-detail">
          <div className="banner-content">
            <div className="title">
              {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.title', {
                t1: (
                  <span className="title-color">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.title_t1')}
                  </span>
                )
              })}
            </div>
            <p>
              {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.content', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.content_t1')}
                  </TextStyle>
                ),
                t2: (
                  <TextStyle variation="strong">
                    <span className="text-gradient">
                      {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.content_t2')}
                    </span>
                  </TextStyle>
                ),
                t3: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBanner.content_t3')}
                  </TextStyle>
                )
              })}
            </p>
            <div>
              <Button
                destructive
                size="slim"
                onClick={handleRegisterCampaign}
                loading={updateTenantAppLoading}>
                {i18n.translate(`AdminHTML.MarketingCampaign.RequestBanner.action`)}
                <img src={arrowRight} alt="action-icon-primary" />
              </Button>
              <span className="text-note">
                {i18n.translate(`AdminHTML.MarketingCampaign.RequestBanner.note`)}
              </span>
            </div>
          </div>
          {/* Render image banner */}
          <div className="image">
            <img src={PersonalizationMarketingCampaign01} alt="Personalization Present Banner" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderVertical = () => (
    <div className="BC-MarketingCampaignBannerVertical">
      <div className="BC-MarketingCampaignBannerVertical-inner">
        <div className="banner-detail">
          {/* Render image banner */}
          <div className="image">
            <img src={PersonalizationMarketingCampaign02} alt="Personalization Present Banner" />
          </div>
          <div className="banner-content">
            <div className="title">
              {i18n.translate('AdminHTML.MarketingCampaign.RequestBannerVertical.title', {
                t1: (
                  <span className="text-gradient">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBannerVertical.title_t1')}
                  </span>
                )
              })}
            </div>
            <p>
              {i18n.translate('AdminHTML.MarketingCampaign.RequestBannerVertical.content', {
                t1: (
                  <TextStyle variation="strong">
                    <span className="text-gradient">
                      {i18n.translate(
                        'AdminHTML.MarketingCampaign.RequestBannerVertical.content_t1'
                      )}
                    </span>
                  </TextStyle>
                ),
                t2: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBannerVertical.content_t2')}
                  </TextStyle>
                ),
                t3: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.MarketingCampaign.RequestBannerVertical.content_t3')}
                  </TextStyle>
                )
              })}
            </p>
            <div>
              <Button
                destructive
                fullWidth
                size="slim"
                onClick={handleRegisterCampaign}
                loading={updateTenantAppLoading}>
                {i18n.translate(`AdminHTML.MarketingCampaign.RequestBannerVertical.action`)}
                <img src={arrowRight} alt="action-icon-primary" />
              </Button>
              <span className="text-note">
                {i18n.translate(`AdminHTML.MarketingCampaign.RequestBannerVertical.note`)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return type === 'horizontal' ? renderHorizontal() : renderVertical();
};

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    tenantApp,
    updateTenantAppLoading: _.get(state.oauth, 'loadingUpdate', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateTenantApp: (body, meta) => dispatch(updateTenantApp(body, meta, ownProps.i18n)),
  updateLocalTenantApp: body => dispatch(updateLocalTenantApp(body))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(MarketingCampaignBanner));
